// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

// Code generated by Microsoft (R) AutoRest Code Generator.
// Changes may cause incorrect behavior and will be lost if the code is regenerated.

import type { PagedAsyncIterableIterator } from "@azure/core-paging";
import type { SimplePollerLike, OperationState } from "@azure/core-lro";
import type {
  FleetResource,
  FleetListOptionalParams,
  FleetListByResourceGroupOptionalParams,
  FleetGetOptionalParams,
  FleetGetResponse,
  FleetCreateOptionalParams,
  FleetCreateResponse,
  FleetUpdateOptionalParams,
  FleetUpdateResponse,
  FleetDeleteOptionalParams,
  FleetDeleteResponse,
} from "../models/index.js";

/// <reference lib="esnext.asynciterable" />
/** Interface representing a Fleet. */
export interface Fleet {
  /**
   * Lists all the fleets under the subscription.
   * @param options The options parameters.
   */
  list(options?: FleetListOptionalParams): PagedAsyncIterableIterator<FleetResource>;
  /**
   * Lists all the fleets under the specified subscription and resource group.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param options The options parameters.
   */
  listByResourceGroup(
    resourceGroupName: string,
    options?: FleetListByResourceGroupOptionalParams,
  ): PagedAsyncIterableIterator<FleetResource>;
  /**
   * Retrieves the properties of an existing Azure Cosmos DB fleet under a subscription
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param fleetName Cosmos DB fleet name. Needs to be unique under a subscription.
   * @param options The options parameters.
   */
  get(
    resourceGroupName: string,
    fleetName: string,
    options?: FleetGetOptionalParams,
  ): Promise<FleetGetResponse>;
  /**
   * Creates an Azure Cosmos DB fleet under a subscription.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param fleetName Cosmos DB fleet name. Needs to be unique under a subscription.
   * @param body The parameters to provide for the current fleet.
   * @param options The options parameters.
   */
  create(
    resourceGroupName: string,
    fleetName: string,
    body: FleetResource,
    options?: FleetCreateOptionalParams,
  ): Promise<FleetCreateResponse>;
  /**
   * Updates the properties of an existing Azure Cosmos DB Fleet.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param fleetName Cosmos DB fleet name. Needs to be unique under a subscription.
   * @param options The options parameters.
   */
  update(
    resourceGroupName: string,
    fleetName: string,
    options?: FleetUpdateOptionalParams,
  ): Promise<FleetUpdateResponse>;
  /**
   * Deletes an existing Azure Cosmos DB Fleet.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param fleetName Cosmos DB fleet name. Needs to be unique under a subscription.
   * @param options The options parameters.
   */
  beginDelete(
    resourceGroupName: string,
    fleetName: string,
    options?: FleetDeleteOptionalParams,
  ): Promise<SimplePollerLike<OperationState<FleetDeleteResponse>, FleetDeleteResponse>>;
  /**
   * Deletes an existing Azure Cosmos DB Fleet.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param fleetName Cosmos DB fleet name. Needs to be unique under a subscription.
   * @param options The options parameters.
   */
  beginDeleteAndWait(
    resourceGroupName: string,
    fleetName: string,
    options?: FleetDeleteOptionalParams,
  ): Promise<FleetDeleteResponse>;
}
